import { useState, useCallback, useMemo } from "react"
import {
  email_account_path,
  email_domains_health_checks_path,
  bulk_email_accounts_path,
  bulk_subscription_email_accounts_path,
  subscription_email_account_path,
} from "~/lib/routes"
import { getCsrfToken } from "~/lib/utils"
import { reconnectEmailAccount, deleteEmailAccount } from "~/react/components/email_accounts/actions"
import { getWarmupStateFromProtection } from "~/react/components/email_accounts/utils"
import type { EmailAccount } from "~/react/components/email_accounts/email_accounts_filters"
import { isPendingAccount } from "../utils"

type PendingAction = {
  type: "bulkDisconnect" | "bulkCancelRenewal" | "individualDisconnect" | "individualCancelRenewal"
  account?: EmailAccount
} | null

type EmailAccountsData = {
  connected?: EmailAccount[]
  managed?: EmailAccount[]
  total?: number
}

type UseBulkActionsArgs = {
  allItems: EmailAccount[]
  selectedIds: Array<string | number>
  selectedCount: number
  clearSelection: () => void
  fetchData: (showLoading?: boolean) => Promise<unknown>
  getNumericIds: (ids: Array<string | number>) => number[]
  isWarmupEnabled: boolean
  isPremium: boolean
  setData: React.Dispatch<React.SetStateAction<EmailAccountsData | null>>
  setActiveAccount: React.Dispatch<React.SetStateAction<EmailAccount | null>>
}

export function useBulkActions({
  allItems,
  selectedIds,
  selectedCount,
  clearSelection,
  fetchData,
  getNumericIds,
  isWarmupEnabled,
  setData,
  setActiveAccount,
}: UseBulkActionsArgs) {
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const [confirmBulkDisconnectOpen, setConfirmBulkDisconnectOpen] = useState(false)
  const [confirmBulkCancelRenewalOpen, setConfirmBulkCancelRenewalOpen] = useState(false)
  const [confirmIndividualDisconnectOpen, setConfirmIndividualDisconnectOpen] = useState(false)
  const [confirmIndividualCancelRenewalOpen, setConfirmIndividualCancelRenewalOpen] = useState(false)
  const [inboxProtectionModalOpen, setInboxProtectionModalOpen] = useState(false)
  const [inboxProtectionAccountIds, setInboxProtectionAccountIds] = useState<number[]>([])

  // Individual actions
  const handleIndividualRunHealthCheck = useCallback(
    async (account: EmailAccount) => {
      // Extract domain from account email
      const accountDomain = account.email.split("@")[1]?.toLowerCase()

      // Immediately update all accounts with the same domain to show pending status
      setData((prev) => {
        if (!prev) return prev
        const updateAccount = (accounts: EmailAccount[] | undefined) =>
          accounts?.map((acc) => {
            const accDomain = acc.email.split("@")[1]?.toLowerCase()
            if (accDomain === accountDomain) {
              return { ...acc, health_check_status: "pending" as const }
            }
            return acc
          })
        return {
          ...prev,
          connected: updateAccount(prev.connected) ?? prev.connected,
          managed: updateAccount(prev.managed) ?? prev.managed,
        }
      })

      // Also update activeAccount if it has the same domain
      setActiveAccount((prev) => {
        if (!prev) return prev
        const prevDomain = prev.email.split("@")[1]?.toLowerCase()
        if (prevDomain === accountDomain) {
          return { ...prev, health_check_status: "pending" as const }
        }
        return prev
      })

      try {
        const response = await fetch(email_domains_health_checks_path(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          credentials: "same-origin",
          body: JSON.stringify({ email_account_ids: [account.id] }),
        })

        if (!response.ok) {
          const error = await response.json()
          alert(error.error || i18n.t("js.email_accounts_center.bulk.health_check_error"))
        }
        // ActionCable will handle updating the health check status when it completes
      } catch {
        alert(i18n.t("js.email_accounts_center.bulk.health_check_error"))
      }
    },
    [setData, setActiveAccount],
  )

  const handleIndividualDisconnect = useCallback((account: EmailAccount) => {
    setPendingAction({ type: "individualDisconnect", account })
    setConfirmIndividualDisconnectOpen(true)
  }, [])

  const executeIndividualDisconnect = useCallback(async (account: EmailAccount) => {
    try {
      const response = await fetch(email_account_path(account.id!), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        App?.displayFlash("success", i18n.t("js.email_accounts_center.bulk.disconnect_success", { count: 1 }))
      } else {
        const error = await response.json()
        alert(error.error || i18n.t("js.email_accounts_center.bulk.disconnect_error"))
      }
    } catch {
      alert(i18n.t("js.email_accounts_center.bulk.disconnect_error"))
    }
  }, [])

  const handleIndividualCancelRenewal = useCallback((account: EmailAccount) => {
    setPendingAction({ type: "individualCancelRenewal", account })
    setConfirmIndividualCancelRenewalOpen(true)
  }, [])

  const executeIndividualCancelRenewal = useCallback(async (account: EmailAccount) => {
    try {
      const response = await fetch(subscription_email_account_path(account.id!), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        App?.displayFlash("success", i18n.t("js.email_accounts_center.bulk.cancel_renewal_success", { count: 1 }))
      } else {
        const error = await response.json()
        App?.displayFlash("error", error.error || i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
      }
    } catch {
      App?.displayFlash("error", i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
    }
  }, [])

  const handleIndividualReconnect = useCallback(
    async (account: EmailAccount) => {
      await reconnectEmailAccount(account, {
        onSuccess: () => fetchData(false),
      })
    },
    [fetchData],
  )

  const handleIndividualDelete = useCallback(async (account: EmailAccount) => {
    await deleteEmailAccount(account.id, {
      confirmKey: "js.email_accounts_center.actions.delete_confirm",
      successKey: "js.email_accounts_center.actions.delete_success",
      errorKey: "js.email_accounts_center.actions.delete_error",
    })
  }, [])

  // Bulk actions
  const handleRunHealthCheck = useCallback(async () => {
    const numericIds = getNumericIds(selectedIds)
    if (numericIds.length === 0) return

    // Immediately update all selected accounts to show pending status
    setData((prev) => {
      if (!prev) return prev
      const updateAccount = (accounts: EmailAccount[] | undefined) =>
        accounts?.map((acc) =>
          numericIds.includes(acc.id ?? -1) ? { ...acc, health_check_status: "pending" as const } : acc,
        )
      return {
        ...prev,
        connected: updateAccount(prev.connected) ?? prev.connected,
        managed: updateAccount(prev.managed) ?? prev.managed,
      }
    })

    // Also update activeAccount if it's selected
    setActiveAccount((prev) => {
      if (!prev || !numericIds.includes(prev.id ?? -1)) return prev
      return { ...prev, health_check_status: "pending" as const }
    })

    try {
      const response = await fetch(email_domains_health_checks_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ email_account_ids: numericIds }),
      })

      if (response.ok) {
        clearSelection()
        // ActionCable will handle updating the health check status when it completes
      } else {
        const error = await response.json()
        alert(error.error || i18n.t("js.email_accounts_center.bulk.health_check_error"))
      }
    } catch {
      alert(i18n.t("js.email_accounts_center.bulk.health_check_error"))
    }
  }, [selectedIds, getNumericIds, clearSelection, setData, setActiveAccount])

  const handleDisconnect = useCallback(() => {
    if (selectedIds.length === 0) return
    setPendingAction({ type: "bulkDisconnect" })
    setConfirmBulkDisconnectOpen(true)
  }, [selectedIds])

  const executeBulkDisconnect = useCallback(async () => {
    const numericIds = getNumericIds(selectedIds)
    if (numericIds.length === 0) return

    try {
      const response = await fetch(bulk_email_accounts_path(), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ ids: numericIds }),
      })

      if (response.status === 202) {
        App?.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.bulk.disconnect_queued", { count: numericIds.length }),
        )
        clearSelection()
      } else if (response.ok) {
        const result = await response.json()
        if (result.errors && result.errors.length > 0) {
          const errorMessages = result.errors
            .map((err: { id: number; errors: string[] }) => err.errors.join(", "))
            .join("\n")
          alert(
            i18n.t("js.email_accounts_center.bulk.disconnect_partial_error", { count: result.destroyed_count }) +
              "\n\n" +
              errorMessages,
          )
        } else {
          App?.displayFlash(
            "success",
            i18n.t("js.email_accounts_center.bulk.disconnect_success", {
              count: result.destroyed_count || numericIds.length,
            }),
          )
        }
        clearSelection()
      } else {
        const error = await response.json()
        alert(error.error || i18n.t("js.email_accounts_center.bulk.disconnect_error"))
      }
    } catch {
      alert(i18n.t("js.email_accounts_center.bulk.disconnect_error"))
    }
  }, [selectedIds, clearSelection, getNumericIds])

  const handleCancelRenewal = useCallback(() => {
    const numericIds = getNumericIds(selectedIds)
    if (numericIds.length === 0) return
    setPendingAction({ type: "bulkCancelRenewal" })
    setConfirmBulkCancelRenewalOpen(true)
  }, [selectedIds, getNumericIds])

  const executeBulkCancelRenewal = useCallback(async () => {
    const numericIds = getNumericIds(selectedIds)
    if (numericIds.length === 0) return

    try {
      const response = await fetch(bulk_subscription_email_accounts_path(), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ ids: numericIds }),
      })

      if (response.status === 202) {
        App?.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.bulk.cancel_renewal_queued", { count: numericIds.length }),
        )
        clearSelection()
      } else if (response.ok) {
        const result = await response.json()
        if (result.errors && result.errors.length > 0) {
          const errorMessages = result.errors
            .map((err: { id: number; errors: string[] }) => err.errors.join(", "))
            .join("\n")
          alert(
            i18n.t("js.email_accounts_center.bulk.cancel_renewal_partial_error", { count: result.cancelled_count }) +
              "\n\n" +
              errorMessages,
          )
        } else {
          App?.displayFlash(
            "success",
            i18n.t("js.email_accounts_center.bulk.cancel_renewal_success", {
              count: result.cancelled_count || numericIds.length,
            }),
          )
        }
        clearSelection()
      } else {
        const error = await response.json()
        App?.displayFlash("error", error.error || i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
      }
    } catch {
      App?.displayFlash("error", i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
    }
  }, [selectedIds, clearSelection, getNumericIds])

  // Warmup
  const warmupEligibleIds = useMemo(() => {
    const selectedAccounts = allItems.filter(
      (account) => selectedIds.includes(account.id) && typeof account.id === "number",
    )
    return selectedAccounts
      .filter((account) => getWarmupStateFromProtection(account.protection) !== "warming_up")
      .map((account) => account.id as number)
  }, [allItems, selectedIds])

  const handleEnableInboxProtection = useCallback(() => {
    if (!isWarmupEnabled || warmupEligibleIds.length === 0) return
    setInboxProtectionAccountIds(warmupEligibleIds)
    setInboxProtectionModalOpen(true)
  }, [isWarmupEnabled, warmupEligibleIds])

  // Compute bulk actions menu
  const bulkActions = useMemo(() => {
    const hasPendingAccounts =
      selectedCount > 0 && allItems.some((account) => selectedIds.includes(account.id) && isPendingAccount(account))
    const numericIds = getNumericIds(selectedIds)
    const hasActionableAccounts = numericIds.length > 0

    // Check if any selected account has a pending health check
    const hasPendingHealthCheck =
      selectedCount > 0 &&
      allItems
        .filter((account) => selectedIds.includes(account.id))
        .some((account) => account.health_check_status === "pending")

    const actions = [
      {
        label: i18n.t("js.email_accounts_center.bulk.run_health_check"),
        icon: "fa-heart-pulse",
        onClick: handleRunHealthCheck,
        disabled: selectedCount === 0 || hasPendingAccounts || hasPendingHealthCheck || !hasActionableAccounts,
      },
      {
        label: i18n.t("js.email_accounts_center.bulk.edit"),
        icon: "fa-pen",
        onClick: () => {}, // placeholder, replaced by caller with handleEdit
        disabled: selectedCount === 0 || hasPendingAccounts || !hasActionableAccounts,
      },
      {
        label: i18n.t("js.email_accounts_center.actions.disconnect"),
        icon: "fa-unlink",
        onClick: handleDisconnect,
        disabled: selectedCount === 0 || hasPendingAccounts || !hasActionableAccounts,
      },
    ]

    if (isWarmupEnabled) {
      actions.splice(1, 0, {
        label: i18n.t("js.email_accounts_center.bulk.enable_inbox_protection"),
        icon: "fa-shield-check",
        onClick: handleEnableInboxProtection,
        disabled: selectedCount === 0 || hasPendingAccounts || warmupEligibleIds.length === 0,
      })
    }

    if (selectedCount > 0 && !hasPendingAccounts && hasActionableAccounts) {
      const selectedAccounts = allItems.filter((account) => selectedIds.includes(account.id))
      const allAreManaged = selectedAccounts.every((acc) => acc.managed === true && !isPendingAccount(acc))

      if (allAreManaged) {
        actions.push({
          label: i18n.t("js.email_accounts_center.actions.cancel_renewal"),
          icon: "fa-times-circle",
          onClick: handleCancelRenewal,
          disabled: selectedCount === 0,
        })
      }
    }

    return actions
  }, [
    selectedIds,
    selectedCount,
    allItems,
    handleCancelRenewal,
    getNumericIds,
    handleEnableInboxProtection,
    warmupEligibleIds.length,
    isWarmupEnabled,
    handleRunHealthCheck,
    handleDisconnect,
  ])

  return {
    // Individual action handlers
    handleIndividualRunHealthCheck,
    handleIndividualDisconnect,
    handleIndividualCancelRenewal,
    handleIndividualReconnect,
    handleIndividualDelete,
    // Individual action executors (for confirmation dialogs)
    executeIndividualDisconnect,
    executeIndividualCancelRenewal,
    // Bulk action executors
    executeBulkDisconnect,
    executeBulkCancelRenewal,
    // Bulk actions list
    bulkActions,
    // Confirmation dialog state
    pendingAction,
    confirmBulkDisconnectOpen,
    setConfirmBulkDisconnectOpen,
    confirmBulkCancelRenewalOpen,
    setConfirmBulkCancelRenewalOpen,
    confirmIndividualDisconnectOpen,
    setConfirmIndividualDisconnectOpen,
    confirmIndividualCancelRenewalOpen,
    setConfirmIndividualCancelRenewalOpen,
    // Inbox protection
    inboxProtectionModalOpen,
    setInboxProtectionModalOpen,
    inboxProtectionAccountIds,
    handleDisconnect,
    handleCancelRenewal,
  }
}
